/**
 * Recipe Ingredients API Client
 * Frontend API calls for managing ingredients linked to recipes
 */

import { apiCall } from '../api';
import type {
  RecipeIngredient,
  AddRecipeIngredientRequest,
  UpdateRecipeIngredientRequest,
  RecipeIngredientsResponse,
  RecipeIngredientResponse,
} from '@/types/api';

/**
 * Fetch all ingredients for a recipe
 */
export async function fetchRecipeIngredients(
  recipeId: number
): Promise<RecipeIngredient[]> {
  const response = await apiCall<RecipeIngredientsResponse>(
    `/api/recipes/${recipeId}/ingredients`,
    { method: 'GET' }
  );
  return response.ingredients;
}

/**
 * Add an ingredient to a recipe
 */
export async function addRecipeIngredient(
  recipeId: number,
  data: AddRecipeIngredientRequest
): Promise<RecipeIngredient> {
  const response = await apiCall<RecipeIngredientResponse>(
    `/api/recipes/${recipeId}/ingredients`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
  return response.recipeIngredient;
}

/**
 * Update an ingredient link in a recipe
 */
export async function updateRecipeIngredient(
  recipeId: number,
  ingredientId: number,
  data: UpdateRecipeIngredientRequest
): Promise<RecipeIngredient> {
  const response = await apiCall<RecipeIngredientResponse>(
    `/api/recipes/${recipeId}/ingredients/${ingredientId}`,
    {
      method: 'PUT',
      body: JSON.stringify(data),
    }
  );
  return response.recipeIngredient;
}

/**
 * Remove an ingredient from a recipe
 */
export async function removeRecipeIngredient(
  recipeId: number,
  ingredientId: number
): Promise<void> {
  await apiCall<{ message: string }>(
    `/api/recipes/${recipeId}/ingredients/${ingredientId}`,
    { method: 'DELETE' }
  );
}
